import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API, WEBSITE_URL, BASE_URL } from "../api";

import {
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiX,
  FiMessageSquare,
  FiFileText,
  FiSend,
  FiTrash2,
  FiClipboard,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";

/* HELPERS */

const formatDate = (date) => {
  if (!date) return "-";

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return date;
  }

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};

const formatAmount = (value) => {
  const amount = Number(value ?? 0);

  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/* COMPONENT */
export default function ManageOnlineBooking() {

  /* TABLE */
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [total,setTotal] = useState(0);

  /* FILTERS */

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [carType, setCarType] = useState("0");
  const [orderBy, setOrderBy] = useState("PDDESC");
  const [searchPeriod, setSearchPeriod] = useState("0");
  const [bookingStatus, setBookingStatus] = useState("m");

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [carGroups, setCarGroups] = useState([]);

  /* VIEW MODAL */

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewBooking, setViewBooking] = useState(null);

  /* COMMENT MODAL */

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentBooking, setCommentBooking] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentEmail, setCommentEmail] = useState("");

  /* RESEND EMAIL MODAL */

  const [showResendModal, setShowResendModal] = useState(false);
  const [resendBooking, setResendBooking] = useState(null);
  const [alternateEmail, setAlternateEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  /* DELETE MODAL */

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBooking, setDeleteBooking] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* CAR GROUPS */

  const fetchCarGroups = useCallback(async () => {
    try {
      const response = await API.get("/online-booking/car");

      if (response.data?.success) {
        setCarGroups(response.data.data || []);
      } else {
        setCarGroups([]);
      }
    } catch (error) {
      console.error("Fetch car groups error:", error);
      setCarGroups([]);
    }
  }, []);

  /* BOOKINGS */

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await API.get("/online-booking/manage", {
        params: {
          page,
          limit,
          fromDate,
          toDate,
          carType,
          orderBy,
          searchPeriod,
          bookingStatus,
          search,
        },
      });

      const result = response.data;

      if (result?.success) {
        const rows = Array.isArray(result.data) ? result.data : [];

        setBookings(rows);

        const total =
          Number(
            result.totalRecords ??
              result.pagination?.totalRecords ??
              result.total ??
              result.count ??
              rows.length
          ) || 0;

        setTotalRecords(total);
        setTotal(Number(total));
        const pages =
          Number(
            result.totalPages ??
              result.pagination?.totalPages ??
              Math.ceil(total / Number(limit || 10))
          ) || 1;

        setTotalPages(pages);
      } else {
        setBookings([]);
        setTotalRecords(0);
        setTotalPages(1);

        toasts.error(result?.message || "Unable to fetch bookings");
      }
    } catch (error) {
      console.error("Fetch bookings error:", error);

      toasts.error(
        error.response?.data?.message || "Unable to connect to server"
      );

      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    fromDate,
    toDate,
    carType,
    orderBy,
    searchPeriod,
    bookingStatus,
    search,
  ]);

  useEffect(() => {
    fetchCarGroups();
  }, [fetchCarGroups]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  /* SEARCH / RESET */

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setCarType("0");
    setOrderBy("PDDESC");
    setSearchPeriod("0");
    setBookingStatus("m");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  /* VIEW BOOKING */

  const handleViewBooking = async (bookingId) => {
    if (!bookingId) {
      toasts.error("Booking ID not found");
      return;
    }

    try {
      setShowViewModal(true);
      setViewLoading(true);
      setViewBooking(null);

      const response = await API.get(`/online-booking/view/${bookingId}`);

      const result = response.data;

      if (result?.success) {
        setViewBooking(result.data);
      } else {
        toasts.error(result?.message || "Unable to fetch booking details");

        setShowViewModal(false);
      }
    } catch (error) {
      console.error("View booking error:", error);

      toasts.error(
        error.response?.data?.message || "Unable to fetch booking details"
      );

      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewBooking(null);
  };

  /* COMMENTS */

  const handleComment = (booking) => {
    setCommentBooking(booking);

    setCommentText(booking?.comments || "");

    setCommentEmail(booking?.online_emailid || booking?.online_email || "");

    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    if (commentLoading) return;

    setShowCommentModal(false);
    setCommentBooking(null);
    setCommentText("");
    setCommentEmail("");
  };

  const handleSaveComment = async () => {
    if (!commentBooking?.bookingid) {
      toasts.error("Booking ID not found");
      return;
    }

    try {
      setCommentLoading(true);

      const response = await API.put(
        `/online-booking/comment/${commentBooking.bookingid}`,
        {
          comments: commentText,
        }
      );

      if (response.data?.success) {
        toasts.success(response.data.message || "Comment updated successfully");

        closeCommentModal();
        fetchBookings();
      } else {
        toasts.error(response.data?.message || "Unable to save comment");
      }
    } catch (error) {
      console.error("Save comment error:", error);

      toasts.error(error.response?.data?.message || "Unable to save comment");
    } finally {
      setCommentLoading(false);
    }
  };

  /* RESEND EMAIL */

  const handleOpenResend = (booking) => {
    setResendBooking(booking);

    setAlternateEmail(
      booking?.optional_emailid || booking?.online_emailid || ""
    );

    setShowResendModal(true);
  };

  /*SEND MAIL BOOKING */

  const closeResendModal = () => {
    if (resendLoading) return;
    setShowResendModal(false);
    setResendBooking(null);
    setAlternateEmail("");
  };

  const handleResendEmail = async () => {
    const bookingId =
      resendBooking?.bookingid ?? resendBooking?.bookingId ?? resendBooking?.id;

    const email = alternateEmail.trim();

    if (!bookingId) {
      toasts.error("Booking ID not found");
      return;
    }

    if (!email) {
      toasts.error("Please enter alternate email");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      toasts.error("Please enter a valid email address");
      return;
    }

    try {
      setResendLoading(true);

      const response = await API.post("/email/resendattachmentemail", {
        bookingid: bookingId,

        online_firstname: resendBooking?.online_firstname || "",

        online_lastname: resendBooking?.online_lastname || "",

        online_title: resendBooking?.online_title || "",

        fullbooking_no: resendBooking?.fullbooking_no || "",

        online_lang: resendBooking?.online_lang || "",
        resend_email: email,

        booking_attachment: resendBooking?.booking_attachment || "",

        location_attachment: resendBooking?.location_attachment || "",
      });

      if (response.data?.success) {
        toasts.success(
          response.data?.message || "Booking Mail Resend Successfully"
        );

        closeResendModal();
        fetchBookings();
      } else {
        toasts.error(
          response.data?.message || "Unable to resend booking email"
        );
      }
    } catch (error) {
      console.error("RESEND EMAIL ERROR:", error);

      toasts.error(
        error.response?.data?.message || "Unable to resend booking email"
      );
    } finally {
      setResendLoading(false);
    }
  };

  /* DELETE BOOKING */

  const handleOpenDelete = (booking) => {
    if (!booking?.bookingid) {
      toasts.error("Booking ID not found");
      return;
    }

    setDeleteBooking(booking);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;

    setShowDeleteModal(false);
    setDeleteBooking(null);
  };

  const handleDelete = async () => {
    if (!deleteBooking?.bookingid) {
      toasts.error("Booking ID not found");
      return;
    }

    try {
      setDeleteLoading(true);

      const response = await API.delete(
        `/online-booking/delete/${deleteBooking.bookingid}`
      );

      if (response.data?.success) {
        toasts.success(response.data.message || "Booking deleted successfully");

        setShowDeleteModal(false);
        setDeleteBooking(null);

        fetchBookings();
      } else {
        toasts.error(response.data?.message || "Unable to delete booking");
      }
    } catch (error) {
      console.error("Delete booking error:", error);

      toasts.error(error.response?.data?.message || "Unable to delete booking");
    } finally {
      setDeleteLoading(false);
    }
  };

  /* PAYMENT STATUS */

  const getPaymentStatus = (booking) => {
    const payStatus = String(booking?.pay_status ?? booking?.payStatus ?? "0");

    const paymentType = String(
      booking?.payment_type ?? booking?.paymentType ?? ""
    );

    if (payStatus === "1") {
      return {
        text: paymentType === "1" ? "Paid(P)" : "Paid(I)",
        className: "status-text text-active",
      };
    }

    return {
      text: "Unpaid",
      className: "status-text text-danger",
    };
  };

  const getBookingDocument = (booking) => booking?.booking_attachment || "-";

  const getLocationDocument = (booking) => booking?.location_attachment || "-";
  // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (loading || page <= 1) {
      return;
    }

    setPage((prev) => Math.max(1, prev - 1));
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (loading || totalPages <= 0 || page >= totalPages) {
      return;
    }

    setPage((prev) => Math.min(totalPages, prev + 1));
  };
  /* RETURN */

  return (
    <Layout activePage="Manage Online Booking">
      <main className="dashboard-main">
        {/* PAGE HEADER */}

        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={24} />

              <span>Manage Online Booking</span>

              <span className="rec-count ms-1">{totalRecords} records</span>
            </h3>

            <button
              type="button"
              className="um-btn-secondary btn-sm"
              onClick={fetchBookings}
              disabled={loading}
            >
              <FiRefreshCw className="me-1" />
              Refresh
            </button>
          </div>

          {/* FILTERS */}

          <div className="p-3 border-bottom bg-light bg-opacity-25">
            <div className="row g-3">
              <div className="col-xl-2 col-md-4">
                <label className="form-label fw-semibold small">
                  From Date
                </label>

                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="col-xl-2 col-md-4">
                <label className="form-label fw-semibold small">To Date</label>

                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="col-xl-2 col-md-4">
                <label className="form-label fw-semibold small">
                  Car Group
                </label>

                <select
                  className="season-input"
                  value={carType}
                  onChange={(e) => {
                    setCarType(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    width: "150px",
                  }}
                >
                  <option value="0">--- ALL ---</option>

                  {carGroups.map((car) => (
                    <option
                      key={car.carmodel_id ?? car.id}
                      value={car.carmodel_id ?? car.id}
                    >
                      {car.carmodel_name ?? car.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-xl-2 col-md-4">
                <label className="form-label fw-semibold small">Order By</label>

                <select
                  className="season-input"
                  value={orderBy}
                  onChange={(e) => {
                    setOrderBy(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    width: "160px",
                  }}
                >
                  <option value="BFASC">Booking Ref ASC</option>

                  <option value="BFDESC">Booking Ref DESC</option>

                  <option value="PDASC">Pick Up Date ASC</option>

                  <option value="PDDESC">Pick Up Date DESC</option>
                </select>
              </div>

              <div className="col-xl-2 col-md-4">
                <label className="form-label fw-semibold small">
                  Search Period
                </label>

                <select
                  className="season-input"
                  value={searchPeriod}
                  onChange={(e) => {
                    setSearchPeriod(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    width: "150px",
                  }}
                >
                  <option value="0">--- All ---</option>

                  <option value="1">Today</option>

                  <option value="2">Last 7 Days</option>

                  <option value="3">Last 30 Days</option>
                </select>
              </div>

              <div className="col-xl-2 col-md-4">
                <label className="form-label fw-semibold small">Status</label>

                <select
                  className="season-input"
                  value={bookingStatus}
                  onChange={(e) => {
                    setBookingStatus(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    width: "150px",
                  }}
                >
                  <option value="m">--- All ---</option>

                  <option value="1">Paid</option>

                  <option value="0">Unpaid</option>
                </select>
              </div>
            </div>

            <div className="row g-3 mt-1 align-items-end">
              <div className="col-lg-7">
                <label className="form-label fw-semibold small">
                  Search By Family Name / Email ID / Booking Reference No. /
                  Regular Client ID
                </label>

                <div className="search-wrapper w-100">
                  <span className="search-icon">
                    <FiSearch />
                  </span>

                  <input
                    type="text"
                    className="search-input"
                    value={searchInput}
                    maxLength={50}
                    placeholder="Search..."
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />

                  {searchInput && (
                    <button
                      type="button"
                      className="season-clear-btn"
                      onClick={() => {
                        setSearchInput("");
                        setSearch("");
                        setPage(1);
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              <div className="col-lg-5">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="um-btn-primary"
                    onClick={handleSearch}
                  >
                    <FiSearch className="me-1" />
                    Search
                  </button>

                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={handleReset}
                  >
                    <FiRefreshCw className="me-1" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* TABLE */}

          <div>
            <table
              className="table table-striped table-hover table-bordered align-middle mb-0"
              style={{
                tableLayout: "fixed",
                width: "100%",
                fontSize: "12.5px",
              }}
            >
              <thead className="table-light">
                <tr className="um-table-head text-center">
                  <th style={{ width: "3%" }} className="text-center p-1">
                    #
                  </th>

                  <th style={{ width: "8%" }} className="text-center p-1">
                    Booking No.
                  </th>

                  <th style={{ width: "11%" }} className="text-center p-1">
                    Name
                  </th>

                  <th style={{ width: "14%" }} className="text-center p-1">
                    Email ID
                  </th>

                  <th style={{ width: "11%" }} className="text-center p-1">
                    Car Model
                  </th>

                  <th style={{ width: "8%" }} className="text-center p-1">
                    Pick Up Date
                  </th>

                  <th style={{ width: "8%" }} className="text-center p-1">
                    Booking Date
                  </th>

                  <th style={{ width: "7.5%" }} className="text-center p-1">
                    Total Fare
                  </th>

                  <th style={{ width: "8%" }} className="text-center p-1">
                    Documents
                  </th>

                  <th style={{ width: "5%" }} className="text-center p-1">
                    Status
                  </th>

                  <th style={{ width: "5%" }} className="text-center p-1">
                    Action
                  </th>

                  <th style={{ width: "11%" }} className="text-center p-1">
                    View
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({
                    length: 6,
                  }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({
                        length: 12,
                      }).map((_, j) => (
                        <td key={j} className="text-center p-2">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : bookings.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={18} />
                      </div>

                      <div className="empty-title">No bookings found</div>
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking, index) => {
                    const bookingId = booking.bookingid;
                    const bookingNo = booking.fullbooking_no;
                    const firstName = booking.online_firstname ?? "-";
                    const lastName = booking.online_lastname ?? "-";
                    const email = booking.online_emailid ?? "-";
                    const clientId = booking.online_clientid ?? "-";
                    const carName = booking.carmodel_name ?? "-";
                    const pickDate = booking.online_pickdate ?? "-";
                    const bookingDate = booking.online_bookingdate ?? "-";
                    const onlinePrice = Number(booking.online_onlineprice ?? 0);
                    const discount = Number(booking.discount ?? 0);
                    const totalFare = onlinePrice - discount;

                    const status = getPaymentStatus(booking);

                    const document = getBookingDocument(booking);

                    const locationDocument = getLocationDocument(booking);

                    const comments = booking.comments || "";

                    return (
                      <tr key={bookingId} className="um-row">
                        <td className="text-center p-1 text-muted">
                          {(page - 1) * limit + index + 1}
                        </td>

                        <td
                          className="text-center p-1"
                          style={{
                            wordBreak: "break-word",
                          }}
                        >
                          <strong
                            className={
                              String(booking.is_modified) === "1"
                                ? "text-danger"
                                : ""
                            }
                          >
                            {bookingNo}
                          </strong>
                        </td>

                        <td
                          className="text-center p-1"
                          style={{
                            wordBreak: "break-word",
                          }}
                        >
                          {lastName || firstName
                            ? `${firstName} ${lastName}`.trim()
                            : "-"}
                        </td>

                        <td
                          className="text-center p-1"
                          style={{
                            wordBreak: "break-all",
                          }}
                        >
                          <div>{email || "-"}</div>

                          {clientId && (
                            <small className="text-success fw-semibold">
                              ({clientId})
                            </small>
                          )}
                        </td>

                        <td
                          className="text-center p-1"
                          style={{
                            wordBreak: "break-word",
                          }}
                        >
                          {carName}
                        </td>

                        <td className="text-center p-1 text-nowrap">
                          {formatDate(pickDate)}
                        </td>

                        <td className="text-center p-1 text-nowrap">
                          {formatDate(bookingDate)}
                        </td>

                        <td className="text-center p-1 text-nowrap">
                          <strong>
                            {Number.isFinite(totalFare)
                              ? `€ ${totalFare.toFixed(2)}`
                              : "-"}
                          </strong>
                        </td>

                        <td className="text-center p-1">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            {document ? (
                              <a
                                // href={`${BASE_URL}/${document.replace(
                                //   /^\/+/,
                                //   ""
                                // )}`} // -> Local
                                href={`${WEBSITE_URL}/admin/${document.replace(/^\/+/, "")}`}  // -> Live
                                target="_blank"
                                rel="noreferrer"
                                className="action-btn action-view btn btn-sm p-1"
                                title="Booking Information"
                              >
                                <FiFileText size={13} />
                              </a>
                            ) : (
                              String(booking.pay_status) === "1" && (
                                <span
                                  className="text-muted"
                                  style={{
                                    fontSize: "10px",
                                  }}
                                >
                                  No voucher
                                </span>
                              )
                            )}

                            {locationDocument && (
                              <a
                                href={`${BASE_URL}/${locationDocument.replace(
                                  /^\/+/,
                                  ""
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                className="action-btn action-view btn btn-sm p-1"
                                title="Voucher Information"
                              >
                                <FiFileText size={13} />
                              </a>
                            )}
                          </div>
                        </td>

                        <td className="um-td text-center p-1">
                          <div className="status-wrap justify-content-center m-0">
                            <span
                              className={status.className}
                              style={{
                                fontSize: "11px",
                              }}
                            >
                              {status.text}
                            </span>
                          </div>
                        </td>

                        <td className="text-center p-1">
                          {document ? (
                            <button
                              type="button"
                              className="action-btn action-view btn btn-sm p-1"
                              title="Resend"
                              onClick={() => handleOpenResend(booking)}
                            >
                              <FiSend size={13} />
                            </button>
                          ) : (
                            <span className="text-muted">--</span>
                          )}
                        </td>

                        <td className="p-1 text-center">
                          <div className="action-buttons d-flex justify-content-center gap-1">
                            {/* VIEW */}

                            <button
                              type="button"
                              className="action-btn action-view btn btn-sm p-1"
                              title="View Booking"
                              onClick={() => handleViewBooking(bookingId)}
                            >
                              <FiEye size={13} />
                            </button>

                            {/* COMMENTS */}

                            <button
                              type="button"
                              className={`action-btn btn btn-sm p-1 ${
                                comments ? "btn-warning" : "action-edit"
                              }`}
                              title="Comments"
                              onClick={() => handleComment(booking)}
                            >
                              <FiMessageSquare size={13} />
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="action-btn action-delete btn btn-sm p-1"
                              title="Delete Booking"
                              onClick={() => handleOpenDelete(booking)}
                            >
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={10}
            loading={loading}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        </div>
      </main>

      {/* ============================================================
          VIEW BOOKING MODAL
      ============================================================ */}

      {showViewModal && (
        <div className="um-overlay" onClick={closeViewModal}>
          <div
            className="um-modal um-view-modal modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="um-modal-head">
              <h2 className="um-modal-title">View Online Booking</h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={closeViewModal}
              >
                <FiX />
              </button>
            </div>

            <div className="um-modal-scroll">
              {viewLoading ? (
                <div className="text-center py-5">
                  <div className="spinner" />

                  <div className="mt-3 text-muted">
                    Loading booking details...
                  </div>
                </div>
              ) : !viewBooking ? (
                <div className="alert alert-warning m-3">
                  No booking details found.
                </div>
              ) : (
                (() => {
                  const booking = viewBooking?.booking || {};

                  const personal = viewBooking?.personalDetails || {};

                  const itinerary = viewBooking?.itinerary || {};

                  const car = viewBooking?.car || {};

                  const calculations = viewBooking?.calculations || {};

                  const payment = viewBooking?.payment || {};

                  const meta = viewBooking?.meta || {};

                  const regularClient = viewBooking?.regularClient || null;

                  const specialDiscount = viewBooking?.specialDiscount || null;

                  const freeExtras = Array.isArray(viewBooking?.freeExtras)
                    ? viewBooking.freeExtras
                    : [];

                  const paidExtras = Array.isArray(viewBooking?.paidExtras)
                    ? viewBooking.paidExtras
                    : [];

                  const freeInsurance = Array.isArray(
                    viewBooking?.freeInsurance
                  )
                    ? viewBooking.freeInsurance
                    : [];

                  const paidInsurance = Array.isArray(
                    viewBooking?.paidInsurance
                  )
                    ? viewBooking.paidInsurance
                    : [];

                  return (
                    <>
                      {/* BOOKING SUMMARY */}

                      <div className="profile-header mb-4">
                        <div className="row g-0 w-100">
                          <div className="col-md-4 p-3 border-end">
                            <div className="form-label fw-semibold">
                              Booking Ref. No.
                            </div>

                            <div className="profile-row-value text-danger fw-bold">
                              {meta.bookingReference ||
                                booking.fullbooking_no ||
                                meta.bookingId ||
                                "—"}
                            </div>
                          </div>

                          <div className="col-md-4 p-3 border-end">
                            <div className="form-label fw-semibold">
                              Booking Date
                            </div>

                            <div className="profile-row-value">
                              {formatDate(
                                meta.bookingDate || booking.online_bookingdate
                              )}
                            </div>
                          </div>

                          <div className="col-md-4 p-3">
                            <div className="form-label fw-semibold">
                              Payment Status
                            </div>

                            <div
                              className={`profile-row-value ${
                                Number(payment.paymentStatus) === 1
                                  ? "text-success fw-bold"
                                  : "text-danger fw-bold"
                              }`}
                            >
                              {Number(payment.paymentStatus) === 1
                                ? "Paid"
                                : "Unpaid"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* PERSONAL INFORMATION */}

                      <div className="sec-div">
                        <span>PERSONAL INFORMATION</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Name</div>

                              <div className="profile-row-value">
                                {/* {personal.title || "—"} */}
                                {personal.firstName || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Family Name</div>

                              <div className="profile-row-value">
                                {personal.lastName || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Email ID</div>

                              <div className="profile-row-value">
                                {personal.email || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Telephone</div>

                              <div className="profile-row-value">
                                {personal.phone || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Mobile Number</div>

                              <div className="profile-row-value">
                                {personal.mobile || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Gender
                              </div>

                              <div className="profile-row-value">
                                {personal.gender || "—"}
                              </div>
                            </div>
                          </div>
                        </div> */}

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Country</div>

                              <div className="profile-row-value">
                                {personal.country || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Address</div>

                              <div className="profile-row-value">
                                {personal.address || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Zip Code</div>

                              <div className="profile-row-value">
                                {personal.postcode || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">City</div>

                              <div className="profile-row-value">
                                {personal.city || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Pick Location Details
                              </div>

                              <div className="profile-row-value">
                                {itinerary.pickupDetails || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Drop Location Details
                              </div>

                              <div className="profile-row-value">
                                {itinerary.dropDetails || "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ITINERARY */}

                      <div className="sec-div mt-4">
                        <span>ITINERARY DETAILS</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Pick Up Location</div>

                              <div className="profile-row-value">
                                {itinerary.pickupLocation || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Pick Up Date</div>

                              <div className="profile-row-value">
                                {formatDate(itinerary.pickupDate)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Pick Up Time</div>

                              <div className="profile-row-value">
                                {itinerary.pickupTime || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Return Location</div>

                              <div className="profile-row-value">
                                {itinerary.returnLocation || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Return Date</div>

                              <div className="profile-row-value">
                                {formatDate(itinerary.returnDate)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Return Time</div>

                              <div className="profile-row-value">
                                {itinerary.returnTime || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Total Duration</div>

                              <div className="profile-row-value">
                                {itinerary.durationDays ?? 0} Days
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Duration Hours</div>

                              <div className="profile-row-value">
                                {itinerary.durationHours ?? 0} Hours
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Driver Age</div>

                              <div className="profile-row-value">
                                {personal.driverAge ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CAR DETAILS */}

                      <div className="sec-div mt-4">
                        <span>CAR DETAILS</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Car Model Name</div>

                              <div className="profile-row-value">
                                {car.name || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Passenger</div>

                              <div className="profile-row-value">
                                {car.passenger ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Luggage</div>

                              <div className="profile-row-value">
                                {car.luggage ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-1">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Doors</div>

                              <div className="profile-row-value">
                                {car.doors ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-1">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">A.C.</div>

                              <div className="profile-row-value">
                                {car.airCondition ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Engine Capacity</div>

                              <div className="profile-row-value">
                                {car.engineCapacity || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Car Image</div>

                              <div className="profile-row-value">
                                {car.image ? (
                                  <img
                                    src={`${BASE_URL}/${car.image}`}
                                    alt={car.image_alt || car.image}
                                    style={{
                                      width: "50%",
                                      borderRadius: "6px",
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  "—"
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* EXTRA DETAILS */}

                      {(freeExtras.length > 0 ||
                        paidExtras.length > 0 ||
                        freeInsurance.length > 0 ||
                        paidInsurance.length > 0) && (
                        <>
                          <div className="sec-div mt-4">
                            <span>EXTRA DETAILS</span>
                          </div>

                          {freeExtras.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Free Optional Extras
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>

                                      <th>Quantity</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {freeExtras.map((item, index) => (
                                      <tr key={item.id || index}>
                                        <td className="text-danger">
                                          {item.name || "—"}
                                        </td>

                                        <td className="text-danger">
                                          {item.quantity ?? 0}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {paidExtras.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Paid Optional Extras
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>

                                      <th>Quantity</th>

                                      <th>Rate/Day</th>

                                      <th>Days</th>

                                      <th>Max Rate</th>

                                      <th>Total Amount</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {paidExtras.map((item, index) => (
                                      <tr
                                        key={item.optdet_id || item.id || index}
                                      >
                                        <td className="text-danger">
                                          {item.optitem_name ||
                                            item.name ||
                                            "—"}
                                        </td>

                                        <td className="text-danger text-center">
                                          {item.quantity ??
                                            item.online_extraitemqty ??
                                            0}
                                        </td>

                                        <td className="text-danger">
                                          €{" "}
                                          {formatAmount(
                                            item.rate ?? item.opt_dayrate ?? 0
                                          )}
                                        </td>

                                        <td className="text-danger text-center">
                                          {item.days ??
                                            itinerary.durationDays ??
                                            0}
                                        </td>

                                        <td className="text-danger">
                                          €{" "}
                                          {formatAmount(
                                            item.maxRate ??
                                              item.opt_maxrate ??
                                              0
                                          )}
                                        </td>

                                        <td className="text-danger fw-semibold">
                                          €{" "}
                                          {formatAmount(item.totalAmount ?? 0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {freeInsurance.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Free Insurance Details
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {freeInsurance.map((item, index) => (
                                      <tr key={item.id || index}>
                                        <td className="text-danger">
                                          {item.name || "—"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {paidInsurance.length > 0 && (
                            <div className="mb-4">
                              <div className="form-label fw-semibold mb-2">
                                Paid Insurance Details
                              </div>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped table-sm">
                                  <thead className="table-light">
                                    <tr className="um-table-head">
                                      <th>Items Name</th>

                                      <th>Rate/Day</th>

                                      <th>Days</th>

                                      <th>Total Amount</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {paidInsurance.map((item, index) => (
                                      <tr
                                        key={item.insdtl_id || item.id || index}
                                      >
                                        <td className="text-danger">
                                          {item.insitem_name ||
                                            item.name ||
                                            "—"}
                                        </td>

                                        <td className="text-danger">
                                          €{" "}
                                          {formatAmount(
                                            item.rate ??
                                              item.online_itemrate ??
                                              0
                                          )}
                                        </td>

                                        <td className="text-danger text-center">
                                          {item.days ??
                                            itinerary.durationDays ??
                                            0}
                                        </td>

                                        <td className="text-danger fw-semibold">
                                          €{" "}
                                          {formatAmount(item.totalAmount ?? 0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* REGULAR CLIENT */}

                      {regularClient && (
                        <>
                          <div className="sec-div mt-4">
                            <span>REGULAR CLIENT DETAILS</span>
                          </div>

                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">Client Id</div>

                                  <div className="profile-row-value">
                                    {regularClient.clientId || "—"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">Client Email</div>

                                  <div className="profile-row-value">
                                    {regularClient.clientEmail || "—"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Discount on per day price
                                  </div>

                                  <div className="profile-row-value text-danger">
                                    {regularClient.discountPercent ?? 0} %
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Discount Amount
                                  </div>

                                  <div className="profile-row-value text-danger">
                                    €{" "}
                                    {formatAmount(regularClient.discountAmount)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* SPECIAL DISCOUNT */}

                      {specialDiscount && (
                        <>
                          <div className="sec-div mt-4">
                            <span>SPECIAL DISCOUNT DETAILS</span>
                          </div>

                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="profile-row">
                                <div>
                                  <div className="form-label">
                                    Per day special discount amount
                                  </div>

                                  <div className="profile-row-value text-danger">
                                    €{" "}
                                    {formatAmount(
                                      specialDiscount.discountAmount
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* RATE CHART */}

                      <div className="sec-div mt-4">
                        <span>RATE CHART DETAILS</span>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-bordered table-striped align-middle">
                          <thead className="table-light">
                            <tr className="um-table-head">
                              <th>Quote</th>

                              <th>Calculation</th>

                              <th>Total Amount</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td className="text-danger">Rental Price</td>

                              <td className="text-danger">
                                € {formatAmount(calculations.rentalPrice)} ×{" "}
                                {calculations.rentalDays ?? 0} Days
                              </td>

                              <td className="text-danger">
                                € {formatAmount(calculations.rentalTotal)}
                              </td>
                            </tr>

                            {Number(calculations.extraHourCharge) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Extra Hour Delay Charges
                                  <br />
                                  (For {calculations.extraHours ?? 0} Hours)
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  € {formatAmount(calculations.extraHourCharge)}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.oneWayRentalCharges) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  One Way Rental Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.oneWayRentalCharges
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.pickupCharge) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Pick Up Location Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  € {formatAmount(calculations.pickupCharge)}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.dropoffCharge) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Drop Off Location Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  € {formatAmount(calculations.dropoffCharge)}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.totalLocationPrice) > 0 && (
                              <tr>
                                <td className="text-danger">
                                  Total Location Charges
                                </td>

                                <td>—</td>

                                <td className="text-danger">
                                  €{" "}
                                  {formatAmount(
                                    calculations.totalLocationPrice
                                  )}
                                </td>
                              </tr>
                            )}

                            {Number(calculations.totalInsurance) > 0 && (
                              <tr>
                                <td className="text-danger">Total Insurance</td>

                                <td>—</td>

                                <td className="text-danger">
                                  € {formatAmount(calculations.totalInsurance)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* TOTAL */}

                      <div className="row g-0 border mt-3">
                        <div className="col-md-6 p-3 fw-semibold">
                          Total Price
                        </div>

                        <div className="col-md-6 p-3 text-end text-danger fs-5 fw-bold">
                          € {formatAmount(calculations.totalPrice)}
                        </div>
                      </div>

                      {/* PAYMENT */}

                      <div className="sec-div mt-4">
                        <span>PAYMENT DETAILS</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Advance Paid</div>

                              <div className="profile-row-value text-danger">
                                {payment.advancePercent ?? 0} % of total price
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Final Total Rental Price
                              </div>

                              <div className="profile-row-value text-danger fw-bold">
                                € {formatAmount(calculations.totalPrice)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">
                                Advance Paid Amount
                              </div>

                              <div className="profile-row-value text-danger fw-bold">
                                − € {formatAmount(payment.advanceAmount)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Balance to Pay</div>

                              <div className="profile-row-value text-danger fw-bold">
                                € {formatAmount(payment.balanceToPay)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="profile-row">
                            <div>
                              <div className="form-label">Payment Status</div>

                              <div className="profile-row-value">
                                {Number(payment.paymentStatus) === 1
                                  ? "Paid"
                                  : "Unpaid"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()
              )}
            </div>

            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={closeViewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          RESEND EMAIL MODAL
      ============================================================ */}

      {showResendModal && (
        <div className="um-overlay" onClick={closeResendModal}>
          <div
            className="um-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "520px",
            }}
          >
            <div className="um-modal-head">
              <h2 className="um-modal-title">Resend Email</h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={closeResendModal}
                disabled={resendLoading}
              >
                <FiX />
              </button>
            </div>

            <div className="um-modal-scroll p-3">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Booking Reference Number
                </label>

                <input
                  type="text"
                  className="form-control"
                  readOnly
                  value={resendBooking?.bookingid || "-"}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Customer Name</label>

                <input
                  type="text"
                  className="form-control"
                  readOnly
                  value={`${resendBooking?.online_firstname || ""} ${
                    resendBooking?.online_lastname || ""
                  }`.trim()}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">First Email</label>

                <input
                  type="email"
                  className="form-control"
                  value={resendBooking?.online_emailid || ""}
                />
              </div>

              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  hidden
                  value={resendBooking?.booking_attachment || "-"}
                />
              </div>

              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  hidden
                  value={resendBooking?.location_attachment || "-"}
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Alternate Email
                </label>

                <input
                  type="email"
                  className="form-control"
                  // value={alternateEmail}
                  onChange={(e) => setAlternateEmail(e.target.value)}
                  placeholder="Enter alternate email"
                  disabled={resendLoading}
                />
              </div>
            </div>

            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={closeResendModal}
                disabled={resendLoading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={handleResendEmail}
                disabled={resendLoading}
              >
                {resendLoading ? (
                  <>
                    <span className="spinner me-1" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend className="me-1" />
                    Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          COMMENT MODAL
      ============================================================ */}

      {showCommentModal && (
        <div className="um-overlay" onClick={closeCommentModal}>
          <div
            className="um-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "600px",
            }}
          >
            <div className="um-modal-head">
              <h2 className="um-modal-title">Comments</h2>

              <button
                type="button"
                className="um-x-btn"
                onClick={closeCommentModal}
                disabled={commentLoading}
              >
                <FiX />
              </button>
            </div>

            <div className="um-modal-scroll p-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Booking Reference Number
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    value={commentBooking?.bookingid || ""}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Customer Name
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    value={`${commentBooking?.online_firstname || ""} ${
                      commentBooking?.online_lastname || ""
                    }`.trim()}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">E-mail ID</label>

                  <input
                    type="email"
                    className="form-control"
                    value={commentEmail}
                    onChange={(e) => setCommentEmail(e.target.value)}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Comments</label>

                  <textarea
                    className="form-control"
                    rows="6"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Enter comments..."
                  />
                </div>
              </div>
            </div>

            <div className="um-modal-foot">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={closeCommentModal}
                disabled={commentLoading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={handleSaveComment}
                disabled={commentLoading}
              >
                {commentLoading ? (
                  <>
                    <span className="spinner me-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSend className="me-1" />
                    Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          DELETE BOOKING MODAL
      ============================================================ */}

      {showDeleteModal && (
        <div className="um-overlay" onClick={closeDeleteModal}>
          <div
            className="um-modal um-delete-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "500px",
            }}
          >
            <div className="delete-content">
              {/* DELETE ICON */}

              <div className="delete-icon">
                <FiTrash2 size={32} />
              </div>

              {/* DELETE TITLE */}

              <h2 className="delete-title">Delete Booking?</h2>

              {/* DELETE DESCRIPTION */}

              <p className="delete-description">
                Are you sure you want to delete{" "}
                <span className="badge badge-success">
                  "
                  {deleteBooking?.fullbooking_no ||
                    deleteBooking?.fullbookingno ||
                    deleteBooking?.booking_no ||
                    deleteBooking?.bookingNo ||
                    deleteBooking?.bookingid ||
                    "this booking"}
                  "
                </span>
                ?
                <br />
                This will permanently remove this booking.
                <br />
                <strong>This action cannot be undone.</strong>
              </p>

              {/* BOOKING INFORMATION */}

              <div className="row g-3 text-start mb-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Booking Reference Number
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    value={
                      deleteBooking?.fullbooking_no ||
                      deleteBooking?.fullbookingno ||
                      deleteBooking?.booking_no ||
                      deleteBooking?.bookingNo ||
                      deleteBooking?.bookingid ||
                      "-"
                    }
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label fw-semibold">
                    Customer Name
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    value={
                      `${deleteBooking?.online_firstname || ""} ${
                        deleteBooking?.online_lastname || ""
                      }`.trim() || "-"
                    }
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label fw-semibold">Email ID</label>

                  <input
                    type="email"
                    className="form-control"
                    readOnly
                    value={
                      deleteBooking?.online_emailid ||
                      deleteBooking?.online_email ||
                      "-"
                    }
                  />
                </div>
              </div>

              {/* DELETE ACTIONS */}

              <div className="delete-actions">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="delete-confirm-btn"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
